import type {
  AnnulOperation,
  BranchChangeset,
  ConfigurationValidationError,
} from '@festina/configuration-general-types/configuration'
import { OperationLayerMap, type WorkspaceKey } from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import { useNavigationParams } from '../../system/useNavigationParams'
import { isEnumMetaType } from '../../types/metaModel'
import type {
  ConfigurationItemsByBlock,
  ConfigurationItemsByLayer,
} from '../../workspace/operationsContext'
import { collectAllNodesFromTree } from '../api/metaModel/helpers'
import { useAllLayersOnServer } from '../api/metaModel/useAllLayersOnServer'
import type { ValidationErrorWithLocation } from '../errors/types'
import type { IndexedRegisteredTenantCreateOperations } from '../registeredTenantCreateOperations/types'
import type { WorkspaceOperationOf } from '../types'
import type { EnumDefinition, EnumLiteral, ModeledEnumLiteral } from './types'
import { useConfiguredEnumDefinitionsWithoutLiterals } from './useConfiguredEnumDefinitionsWithoutLiterals'
import { useConfiguredEnumLiterals } from './useConfiguredEnumLiterals'

export const useConfiguredEnums = ({
  configurationChangesets,
  workspaceKey,
  errors,
  allTenants,
  draftAnnulOperations,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  workspaceKey: Optional<WorkspaceKey>
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  allTenants: Optional<
    ConfigurationItemsByBlock<IndexedRegisteredTenantCreateOperations>
  >
  draftAnnulOperations: WorkspaceOperationOf<AnnulOperation>[]
}): Map<string, ConfigurationItemsByLayer<EnumDefinition>> => {
  const { blockTopology } = useMetaModelContext()
  const configuredLiterals = useConfiguredEnumLiterals({
    configurationChangesets,
    workspaceKey,
    errors,
    draftAnnulOperations,
  })
  const configuredEnumDefinitions = useConfiguredEnumDefinitionsWithoutLiterals(
    {
      configurationChangesets,
      workspaceKey,
      errors,
      draftAnnulOperations,
    },
  )
  const { server, layer } = useNavigationParams()
  const allLayers = useAllLayersOnServer({
    server,
    allTenants,
  })
  const allBlocks = useMemo(
    () => (blockTopology ? collectAllNodesFromTree(blockTopology) : []),
    [blockTopology],
  )

  // We consider only the modeled enums on the layer that's currently open in the portal. Modeled enums are really only relevant inside the enum viewer.
  // All other places where enums operations are loaded we're really only interested in the configured enums.
  const metaModel = useLazyLayerMetaModel(layer.layerId)
  const modeledEnums = useMemo(
    () => Array.from(metaModel.types.values()).filter(isEnumMetaType),
    [metaModel.types],
  )

  return useMemo(
    () =>
      new Map<string, ConfigurationItemsByLayer<EnumDefinition>>(
        allBlocks.map((block) => {
          const layersOnBlock = allLayers.get(block)
          assertNotEmpty(layersOnBlock)

          return [
            block,
            new OperationLayerMap<Map<string, EnumDefinition>>(
              layersOnBlock
                .map((branchLayer) => {
                  const configuredLiteralsOnLayer = configuredLiterals
                    .get(block)
                    ?.get(branchLayer)

                  const configuredEnumDefinitionsOnLayer =
                    configuredEnumDefinitions.get(block)?.get(branchLayer)

                  const enumDefinitionsOnLayer = Array.from(
                    new Set([
                      ...(configuredEnumDefinitionsOnLayer != null
                        ? Array.from(configuredEnumDefinitionsOnLayer.keys())
                        : []),
                      ...(notEmpty(configuredLiteralsOnLayer)
                        ? Array.from(configuredLiteralsOnLayer.keys()).map(
                            (key) => key.split('.')[0],
                          )
                        : []),
                    ]),
                  )

                  return [
                    branchLayer,
                    new Map<string, EnumDefinition>(
                      enumDefinitionsOnLayer
                        .map((enumName) => {
                          const enumDefinitionOnLayer =
                            configuredEnumDefinitionsOnLayer?.get(enumName)

                          const literalKeysForEnum = Array.from(
                            configuredLiteralsOnLayer?.keys() ?? [],
                          ).filter((key) => key.startsWith(`${enumName}.`))

                          const matchedLiteralsForEnum: EnumLiteral[] =
                            literalKeysForEnum.flatMap(
                              (key) =>
                                configuredLiteralsOnLayer?.get(key) ?? [],
                            )

                          const matchedDefinition: EnumDefinition =
                            enumDefinitionOnLayer
                              ? {
                                  ...enumDefinitionOnLayer,
                                  literals: matchedLiteralsForEnum,
                                }
                              : {
                                  name: enumName,
                                  internalOperation: undefined,
                                  changesetHistory: undefined,
                                  layer: branchLayer,
                                  actions: [],
                                  literals: matchedLiteralsForEnum,
                                  errors: [],
                                  annulOperations: [],
                                  isDraft: false,
                                  block,
                                }

                          const hasConfigurationOnCurrentLayer =
                            (matchedDefinition.layer.equals(branchLayer) &&
                              matchedDefinition.internalOperation) ||
                            matchedDefinition.literals.some(
                              (literal) => literal.type === 'configured',
                            )

                          if (!hasConfigurationOnCurrentLayer) {
                            return [enumName, undefined] as const
                          }

                          const modeledLiteralsForEnum = modeledEnums.find(
                            (modeledEnum) => modeledEnum.typeName === enumName,
                          )

                          modeledLiteralsForEnum?.values?.forEach(
                            (modeledLiteral) => {
                              if (
                                !matchedLiteralsForEnum.some(
                                  (literal) =>
                                    literal.type === 'configured' &&
                                    literal.internalOperation.entity?.name ===
                                      modeledLiteral,
                                )
                              ) {
                                matchedLiteralsForEnum.push({
                                  type: 'modeled',
                                  literalName: modeledLiteral,
                                  enumDefinition: modeledLiteralsForEnum,
                                } satisfies ModeledEnumLiteral)
                              }
                            },
                          )

                          return [enumName, matchedDefinition] as const
                        })
                        .filter(([_, definition]) => !!definition) as [
                        string,
                        EnumDefinition,
                      ][],
                    ),
                  ] as const
                })
                .filter(([, definitions]) => !!definitions.size),
            ),
          ]
        }),
      ),
    [
      configuredLiterals,
      configuredEnumDefinitions,
      allLayers,
      modeledEnums,
      allBlocks,
    ],
  )
}
